import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** A phase status plus its follow-up reminder cadence. */
export interface StatusReminderConfig {
    statusId: string;
    statusName: string;
    phaseId?: string;
    color?: string;
    enabled: boolean;
    frequencyDays: number;
    maxDays: number;
}

/** Cadence state of a case on its current status (for the per-case reminder chip). */
export interface ReminderState {
    configured: boolean;
    statusId?: string;
    statusName?: string;
    frequencyDays?: number;
    maxDays?: number;
    remindersSent?: number;
    daysOnStatus?: number;
    nextReminderInDays?: number | null;
    exhausted?: boolean;
    done?: boolean;
    lastReminderSent?: boolean;
}

/**
 * Pro follow-up reminder cadence API (workflow-definer /api/reminders). Lists
 * every status with its cadence (defaults applied when unset) and saves one
 * status's cadence at a time.
 */
@Injectable({ providedIn: 'root' })
export class ReminderCadenceService {
    private http = inject(HttpClient);
    private base = `${environment.apiBaseUrl}/api/reminders`;

    /** All statuses + their cadence (enabled / frequencyDays / maxDays). */
    listConfig(): Observable<StatusReminderConfig[]> {
        return this.http.get<any>(`${this.base}/config`).pipe(
            map(res => (res?.data ?? res ?? []) as StatusReminderConfig[])
        );
    }

    /** Enable/tune the cadence for one status. */
    saveConfig(statusId: string, body: { enabled: boolean; frequencyDays: number; maxDays: number }) {
        return this.http.put<any>(`${this.base}/config/${statusId}`, body).pipe(
            map(res => (res?.data ?? res) as StatusReminderConfig)
        );
    }

    /** Cadence state for a case on its current status. */
    getCaseState(caseId: string): Observable<ReminderState> {
        return this.http.get<any>(`${this.base}/case/${caseId}`).pipe(
            map(res => (res?.data ?? res) as ReminderState)
        );
    }

    /** Restart the reminder clock for the case's current status. */
    resetCase(caseId: string): Observable<ReminderState> {
        return this.http.post<any>(`${this.base}/case/${caseId}/reset`, {}).pipe(
            map(res => (res?.data ?? res) as ReminderState)
        );
    }

    /** Stop reminders for the case's current status. */
    markCaseDone(caseId: string): Observable<ReminderState> {
        return this.http.post<any>(`${this.base}/case/${caseId}/done`, {}).pipe(
            map(res => (res?.data ?? res) as ReminderState)
        );
    }
}
